import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse, IModuleFilter, ITableQuery } from '../../types';
import { parseQueryParams } from '../../utils';
import { IApprovalConfigPayload } from '../../pages/Requisitions/constants/requisition.interface';
import { SystemModule } from '../../enums';
import {
  ICustomerReportFilterFormProps,
  TCustomerStatementFilterProps,
} from '../../pages/CustomerStatement/constants/CustomerReportsInterface';
import { baseQueryWithReauth } from './baseQuery';

const reportsApi = createApi({
  reducerPath: 'reports',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['CustomerStatement', 'ReportConfig', 'ReportConfigAll'],
  endpoints: builder => ({
    getCustomerStatement: builder.query<
      BackendResponse<{
        list: any[];
        count?: number;
        openingBalance: number;
      }>,
      TCustomerStatementFilterProps
    >({
      query: params => {
        return {
          url: `/reports/customer-statement${parseQueryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: ['CustomerStatement'],
    }),
    getCustomerStatementReportToExcel: builder.query<
      string,
      ICustomerReportFilterFormProps
    >({
      query: params => {
        return {
          url: `/reports/customer-statement/excel${parseQueryParams(params)}`,
          method: 'GET',
          responseHandler: async response =>
            URL.createObjectURL(await response.blob()),
          cache: 'no-cache',
        };
      },
    }),
    getActiveReportConfig: builder.query<BackendResponse<any>, IModuleFilter>({
      query: params => ({
        url: `/reports/config${parseQueryParams(params)}`, // Endpoint URL
        method: 'GET',
      }),
      providesTags: (_, err, params) => {
        if (err) {
          return [];
        }
        return [{ type: 'ReportConfig', id: params?.module }];
      },
    }),
    getAllReportConfig: builder.query<BackendResponse<any>, ITableQuery>({
      query: params => ({
        url: `/reports/config/all${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: (_, error, params) => {
        if (error) {
          return [];
        }
        return [{ type: 'ReportConfigAll', id: params?.module }];
      },
    }),

    createReportConfig: builder.mutation<
      BackendResponse<void>,
      IApprovalConfigPayload
    >({
      query: payload => ({
        url: `/reports/config`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) => {
        if (error) {
          return [];
        }
        return [{ type: 'ReportConfigAll', id: payload?.module }];
      },
    }),
    updateReportConfig: builder.mutation<
      BackendResponse<void>,
      IApprovalConfigPayload
    >({
      query: payload => ({
        url: `/reports/config`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_, error, payload) => {
        if (error) {
          return [];
        }
        return [
          { type: 'ReportConfig', id: payload?.module },
          { type: 'ReportConfigAll', id: payload?.module },
        ];
      },
    }),
    deleteReportConfig: builder.mutation<
      BackendResponse<void>,
      { id: number; module: SystemModule }
    >({
      query: ({ id, module }) => ({
        url: `/reports/config/${id}`,
        method: 'DELETE',
        params: { module },
      }),
      // Invalidates the relevant tags after the delete operation
      invalidatesTags: (_, error, params) => {
        if (error) {
          return [];
        }
        return [{ type: 'ReportConfigAll', id: params?.module }];
      },
    }),
    updateReportGlobalVariantConfig: builder.mutation<
      BackendResponse<void>,
      { id: number; module: SystemModule }
    >({
      query: ({ id, module }) => {
        return {
          url: `/reports/config/global/${id}`,
          method: 'PUT',
          body: {
            module,
          },
        };
      },
      invalidatesTags: (_, error, payload) => {
        if (error) {
          return [];
        }
        return [
          { type: 'ReportConfig', id: payload?.module },
          { type: 'ReportConfigAll', id: payload?.module },
        ];
      },
    }),
    updateReportDefaultVariantConfig: builder.mutation<
      BackendResponse<void>,
      { id: number; module: SystemModule }
    >({
      query: ({ id, module }) => {
        return {
          url: `/reports/config/default/${id}`,
          method: 'PUT',
          body: {
            module,
          },
        };
      },
      invalidatesTags: (_, error, payload) => {
        if (error) {
          return [];
        }
        return [
          { type: 'ReportConfig', id: payload?.module },
          { type: 'ReportConfigAll', id: payload?.module },
        ];
      },
    }),
  }),
});

export const {
  useGetCustomerStatementQuery,
  useGetActiveReportConfigQuery,
  useGetAllReportConfigQuery,
  useCreateReportConfigMutation,
  useUpdateReportConfigMutation,
  useDeleteReportConfigMutation,
  useUpdateReportGlobalVariantConfigMutation,
  useUpdateReportDefaultVariantConfigMutation,
  useLazyGetCustomerStatementReportToExcelQuery,
} = reportsApi;

export default reportsApi;
